import _ from "lodash";
import React, { useCallback, useRef, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import classNames from "classnames";
import classes from "./DraggableCell.scss";
import { DotsSixVerticalOutlined } from "@toddle-edu/ds-icons";
import { CSS } from "@dnd-kit/utilities";
import PropTypes from "prop-types";
import { getDraggableCellParams } from "../utils";
import { getStyleStrippedText } from "Utils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { Checkbox } from "@toddle-edu/ds-web";
import { COL_CHECKBOX } from "UnitPlans/components/IBcomponents/ActionTableV2/ActionTableV2Utils";
import AutoResizeTextarea from "../../AutoResizeTextarea";
import { useI18n } from "Hooks";
import { DOC_EDITOR_STYLES } from "Worksheet/modules/WorksheetConstants";
import MiniDocEditor from "UIComponents/DocEditor/MiniDocEditorWrapper";

const DraggableCell = ({
  cell,
  setCellValue,
  mode,
  hasColHeader,
  colIndex,
  rowIndex,
  hasRowHeader,
  additionalClassesCell,
  suffixElementCell,
  setColumnOptionsPosition,
  setShowColumnOptions,
  setShowRowOptions,
  showColumnOptions,
  getTableActivePrarms,
  rowActive,
  customCellHeaderStyles,
  showDisabledCellClasses,
  additionalCellStyles,
  testId,
  customActiveCellStyles,
}) => {
  const { t } = useI18n(["common"]);
  const showDocEditor = getIsWorksheetDocEditorEnabled();
  const columId = _.get(cell, ["column", "id"]);
  const rowId = _.get(cell, ["row", "id"]);
  const accessorKey = _.get(cell, ["column", "columnDef", "accessorKey"]);
  const isCheckbox = _.isEqual(accessorKey, COL_CHECKBOX);
  const isCreateMode = _.isEqual(mode, "create");
  const totalCols = _.size(cell?.row?.getVisibleCells());
  const isInactiveQuestion = _.includes(["restricted-view", "preview"], mode);

  const {
    isDragging,
    setNodeRef,
    transform,
    attributes,
    listeners,
  } = useSortable({
    id: columId,
  });

  useEffect(() => {
    if (rowIndex === 0) {
      _.get(cell.getContext(), ["table", "options", "dragHandleRefsCol"], {})[
        columId
      ] = dragHandleRef;
    }
  }, [cell]);

  useEffect(() => {
    if (!transform) return;
    !_.inRange(_.get(transform, "x"), -2, 2) ||
    !_.inRange(_.get(transform, "y"), -2, 2)
      ? setShowColumnOptions(-1)
      : null;
  }, [transform]);
  // reference for drag handle, to get click event
  const dragHandleRef = useRef();
  // reference for input cell, to get clickOutside event
  const cellRef = useRef();

  const isHeader =
    (hasColHeader && colIndex == 0) || (hasRowHeader && rowIndex == 0);

  useEffect(() => {
    let isClicked = true;
    // starting of click
    function mouseDown(e) {
      isClicked = true;
    }
    // if mouse moves, it is drag so clicked is false
    function mouseMove(e) {
      isClicked = false;
    }
    // if mouse clicked then only show Column Options Overlay
    function mouseUp(e) {
      if (isClicked) {
        // setting columnOptions to id of column to know which column to show for column options overlay
        setShowColumnOptions(columId);
        if (dragHandleRef?.current && rowIndex == 0) {
          const rect = dragHandleRef?.current?.getBoundingClientRect();
          // getting the column position in window, to set column options overlay
          setColumnOptionsPosition({
            top: rect?.top,
            left: rect?.left,
            width: rect?.width,
            height: rect?.height,
            colIndex: colIndex,
            columId: columId,
          });
        }
      }

      // Reset this back to false for next time
      isClicked = false;
    }

    // // reposition overlay on window resize
    function repositionOverlay(e) {
      if (
        dragHandleRef?.current &&
        rowIndex == 0 &&
        _.isEqual(showColumnOptions, columId)
      ) {
        const rect = dragHandleRef?.current?.getBoundingClientRect();
        // getting the column position in window, to set column options overlay
        setColumnOptionsPosition({
          top: rect?.top,
          left: rect?.left,
          width: rect?.width,
          height: rect?.height,
          colIndex: colIndex,
        });
      }
    }

    if (dragHandleRef && dragHandleRef?.current) {
      dragHandleRef?.current?.addEventListener("mousedown", mouseDown);
      dragHandleRef?.current?.addEventListener("mousemove", mouseMove);
      dragHandleRef?.current?.addEventListener("mouseup", mouseUp);
      window.addEventListener("resize", repositionOverlay);
      window.addEventListener("wheel", repositionOverlay);

      return () => {
        // cleanup functions
        if (dragHandleRef && dragHandleRef?.current) {
          dragHandleRef?.current?.removeEventListener("mousedown", mouseDown);
          dragHandleRef?.current?.removeEventListener("mouseup", mouseUp);
          dragHandleRef?.current?.removeEventListener("mousemove", mouseMove);
          window.removeEventListener("resize", repositionOverlay);
          window.removeEventListener("wheel", repositionOverlay);
        }
      };
    }
  }, [dragHandleRef?.current, showColumnOptions]);

  useEffect(() => {
    // This use effect will run when click on any cell expect drag handle, to close column and row options overlay.
    function mouseDown(e) {
      setShowColumnOptions(-1);
      setShowRowOptions(-1);
    }
    if (cellRef && cellRef?.current) {
      cellRef?.current?.addEventListener("mousedown", mouseDown);
    }
    return () => {
      if (cellRef && cellRef?.current) {
        cellRef?.current?.removeEventListener("mousedown", mouseDown);
      }
    };
  }, [cellRef?.current]);

  const {
    isInputCell,
    cellVal,
    isDisabled,
    showDisabledCell,
    isAnswerVariable,
  } = getDraggableCellParams({
    cell,
    mode,
    isHeader,
  });

  // params to help with setting active column border (showing column options overlay)
  const { leftOfActiveCol, bottomRowIndex } = getTableActivePrarms() || {};

  const style = {
    position: "relative",
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: "width transform 0.2s ease-in-out",
    width: 100 / (_.size(_.get(cell, ["row", "original"])) - 1) + "%",
    zIndex: isDragging ? 1 : 0,
    ...(isDragging &&
      !isHeader && { backgroundColor: "var(--surface-primary-enabled)" }),
    ...(rowActive || _.isEqual(columId, showColumnOptions)
      ? customActiveCellStyles
      : {}),
    ...(isHeader && customCellHeaderStyles),
    ...additionalCellStyles,
  };

  const headerClassnames = classNames(...additionalClassesCell, {
    [classes.tableCell]: true,
    [classes.tableCellActive]: _.isEqual(columId, showColumnOptions),
    [classes.activeCellBackground]: rowActive,
    [classes.tableCellDisabled]:
      isDisabled && showDisabledCell && showDisabledCellClasses,
    [classes.tableCellActiveBorderRight]: _.isEqual(columId, leftOfActiveCol),
    [classes.tableCellActiveFirstRow]:
      _.isEqual(columId, showColumnOptions) && _.isEqual(rowIndex, 0),
    [classes.tableCellActiveLastRow]:
      _.isEqual(columId, showColumnOptions) && _.isEqual(rowId, bottomRowIndex),
    [classes.topLeftCell]: rowIndex === 0 && colIndex === 0,
    [classes.topRightCell]:
      rowIndex === 0 &&
      colIndex === _.size(_.get(cell, ["row", "original"])) - 2,
    [classes.bottomLeftCell]:
      rowIndex === _.size(cell.getContext().table.options.data) - 1 &&
      colIndex === 0,
    [classes.bottomRightCell]:
      rowIndex === _.size(cell.getContext().table.options.data) - 1 &&
      colIndex === _.size(_.get(cell, ["row", "original"])) - 2,
    [classes.tableCellV2]: isInactiveQuestion,
    [classes.header]: isHeader,
    [classes.inputTextHighlight]: isInactiveQuestion && isAnswerVariable,
  });

  const onMouseEnter = useCallback(() => {
    if (_.isEqual(mode, "create")) {
      _.set(
        _.get(cell.getContext(), [
          "table",
          "options",
          "dragHandleRefsCol",
          columId,
          "current",
          "style",
        ]),
        "visibility",
        "visible"
      );
    }
  }, [cell, columId]);

  const onMouseLeave = useCallback(() => {
    if (_.isEqual(mode, "create"))
      if (_.isEqual(showColumnOptions, columId)) {
        _.set(
          _.get(cell.getContext(), [
            "table",
            "options",
            "dragHandleRefsCol",
            columId,
            "current",
            "style",
          ]),
          "visibility",
          "visible"
        );
      } else {
        _.set(
          _.get(cell.getContext(), [
            "table",
            "options",
            "dragHandleRefsCol",
            columId,
            "current",
            "style",
          ]),
          "visibility",
          "hidden"
        );
      }
  }, [showColumnOptions]);

  useEffect(() => {
    if (!_.isEqual(showColumnOptions, columId)) {
      _.set(
        _.get(cell.getContext(), [
          "table",
          "options",
          "dragHandleRefsCol",
          columId,
          "current",
          "style",
        ]),
        "visibility",
        "hidden"
      );
    }
  }, [showColumnOptions]);

  const onChangeInputForTextarea = e => {
    setCellValue({
      rowId,
      colId: accessorKey,
      val: _.get(e, "target.value"),
      isInputCell: isInputCell,
    });
  };

  const onCheckboxClick = e => {
    setCellValue({
      rowId,
      colId: accessorKey,
      val: e.target.checked,
      isInputCell: isInputCell,
    });
  };

  const onChangeInputForDocEditor = useCallback(
    (value, isEmpty) => {
      setCellValue({
        rowId,
        colId: accessorKey,
        val: isEmpty ? "" : value,
        isInputCell,
      });
    },
    [setCellValue, rowId, accessorKey, isInputCell]
  );

  const dragHandleClasses = classNames({
    [classes.hide]: !_.get(cell, ["column", "columnDef", "showToolbar"], true),
  });

  const tableCellValueClasses = classNames({
    [classes.tableCellValueDisabled]: isDisabled,
  });

  return (
    <td
      style={style}
      ref={node => {
        setNodeRef(node);
        cellRef.current = node;
      }}
      className={headerClassnames}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-test-id={`${testId}-draggableCell-tableCell`}
      key={cell.id}
    >
      {isCheckbox ? (
        !_.isNull(cellVal) ? (
          <div className={classes.checkbox}>
            <Checkbox
              isChecked={cellVal}
              onChange={onCheckboxClick}
              disabled={!isCreateMode}
            />
          </div>
        ) : null
      ) : (
        <div>
          {showDocEditor && !isDisabled ? (
            <MiniDocEditor
              saveDelay={300}
              isSlashMenuEnabled={true}
              value={cellVal}
              width="100%"
              minHeight={56}
              updateInputField={onChangeInputForDocEditor}
              editorClassName={classes.docEditorInCell}
              containerStyle={DOC_EDITOR_STYLES.MINI_EDITOR_CONTAINER_STYLES}
              styles={DOC_EDITOR_STYLES.MINI_EDITOR_TABLE_STYLES}
              floatingMiniEditorToolbarConfig={{ isEnabled: true }}
              isPortalToBody={true}
            />
          ) : showDocEditor && isDisabled ? (
            <div
              style={
                DOC_EDITOR_STYLES.MINI_EDITOR_TABLE_STYLES
                  .contentEditableWrapper
              }
              className={classes.docEditorInCell}
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: cellVal || "",
                  type: "DOCUMENT_EDITOR",
                }),
              }}
            />
          ) : (
            <AutoResizeTextarea
              dir="auto"
              value={cellVal}
              disabled={isDisabled}
              onChange={onChangeInputForTextarea}
              data-test-id={`${testId}-draggableCell-tableCellInput`}
              minHeight={36}
              className={tableCellValueClasses}
              reCalculateHeight={mode}
              totalCols={totalCols}
              aria-label={t("common:table_cell_label", {
                row: rowIndex + 1,
                col: colIndex + 1,
              })}
            />
          )}
          {suffixElementCell}
        </div>
      )}
      {rowIndex === 0 && isCreateMode ? (
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          dragHandleRef={dragHandleRef}
          testId={testId}
          dragHandleClasses={dragHandleClasses}
        />
      ) : null}
    </td>
  );
};

const areEqual = (prevProps, nextProps) => {
  return _.isEqual(
    _.omitBy(prevProps, (val, key) => {
      return (
        (typeof val === "function" &&
          !_.includes(["setCellValue", "getTableActivePrarms"], key)) ||
        key === "listeners"
      );
    }),
    _.omitBy(nextProps, (val, key) => {
      return (
        (typeof val === "function" &&
          !_.includes(["setCellValue", "getTableActivePrarms"], key)) ||
        key === "listeners"
      );
    })
  );
};
const DragHandle = React.memo(
  ({ attributes, listeners, dragHandleRef, testId, dragHandleClasses }) => {
    return (
      <span
        ref={node => (dragHandleRef.current = node)}
        {...attributes}
        {...listeners}
        data-test-id={`${testId}-draggableCell-columnHandle`}
        className={dragHandleClasses}
      >
        <DotsSixVerticalOutlined />
      </span>
    );
  },
  areEqual
);

DragHandle.displayName = "DragHandle";

DraggableCell.propTypes = {
  cell: PropTypes.object,
  setCellValue: PropTypes.func,
  mode: PropTypes.string,
  hasColHeader: PropTypes.bool,
  colIndex: PropTypes.number,
  rowIndex: PropTypes.number,
  hasRowHeader: PropTypes.bool,
  answer: PropTypes.string,
  testId: PropTypes.string,
  isInactiveQuestionRevamp: PropTypes.bool,
};

DraggableCell.defaultProps = {
  isInactiveQuestionRevamp: false,
};

export default React.memo(DraggableCell, areEqual);
